import _ from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getErrorMessage,
  hasValidationErrors,
} from '../../../../common/global/helpers';
import useBreakpoints from '../../../../common/hooks/useBreakpoints';
import Breadcrumbs from '../../../../common/widgets/Breadcrumbs';
import { ErrorPanel } from '../../../../common/widgets/ErrorPanel';
import Loader from '../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../common/widgets/Notification';
import { TabInfo } from '../../../../common/widgets/TabStrip';
import { CustomerInfoContext } from '../../contexts/CustomerInfoContext';
import { PlatformInfoContext } from '../../contexts/PlatformInfoContext';
import { DyPageTypeEnum, ProductTypes } from '../../enums';
import { ErrorState, ErrorType } from '../../models/Errors';
import { PaymentMethodModel } from '../../models/PaymentMethods';
import {
  BundleProductModel,
  DefaultProductDetailsModel,
  ElectionWindowInfoModel,
  ProductDetailsBaseModel,
} from '../../models/Product';
import { ProductPaymentMethodDetailsModel } from '../../models/ProductPaymentMethodDetailsModel';
import { resources } from '../../resources/resources';
import { productsService } from '../../services/products';
import { SmartNotifications } from '../../widgets/SmartNotifications';
import applicationCrumbs from '../ApplicationCrumbs';
import { LimitErrorDetailsDialog } from '../Common/Dialogs/LimitErrorDetailsDialog';
import RecommendedProducts from '../Common/RecommendedProducts';
import BundleProductsCarousel from './components/BundleProductsCarousel';
import DetailsTab from './components/DetailsTab';
import { PlatformClosedDetailsDialog } from './components/Dialogs/PlatformClosedDetailsDialog';
import MyCards from './components/MyCards';
import NotFound from './components/NotFound';
import PrivacyInfo from './components/PrivacyInfo';
import ProductBundleInfo from './components/ProductBundleInfo';
import ProductCard from './components/ProductCard';
import ProductDetailsForm from './components/ProductDetailsForm';
import {
  getBreadcrumbs,
  mapProductDetailsProps,
} from './components/ProductFormMappers';
import { CustomerBudgetContext } from '../../contexts/CustomerBudgetContext';

enum ProductPageDialogs {
  PlatformClosed = 1,
  LimitErrorDetails = 2,
}

type ProductWithBundleProducts = ProductDetailsBaseModel & {
  bundleProducts?: BundleProductModel[];
};

export default function ProductsPage() {
  const navigate = useNavigate();
  const { customerInfo } = useContext(CustomerInfoContext);
  const { productId } = useParams();
  const { t: translate } = useTranslation();
  const { isMobile } = useBreakpoints();
  const crumbs = applicationCrumbs(translate);
  const [product, setProduct] = useState<ProductDetailsBaseModel>();
  const [tabs, setTabs] = useState<TabInfo[]>([]);
  const [paymentMethodDetails, setPaymentMethodDetails] = useState<
    ProductPaymentMethodDetailsModel[]
  >([] as ProductPaymentMethodDetailsModel[]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodModel[]>(
    [] as PaymentMethodModel[],
  );
  const [errors, setErrors] = useState<ErrorState>({});
  const platformInfo = useContext(PlatformInfoContext);
  const [visibleDialogId, setVisibleDialogId] = useState<number | undefined>();
  const [paymentError, setPaymentError] = useState<string | undefined>(
    undefined,
  );
  const [errorType, setErrorType] = useState<ErrorType | undefined>();
  const { customerBudget, updateCustomerBudgetCashback } = useContext(
    CustomerBudgetContext,
  );
  const isPayableByCard =
    !!product &&
    product.productTypeId === ProductTypes.DefaultProduct &&
    (product as DefaultProductDetailsModel).isPayableByCard;

  const bundleProducts =
    (product as ProductWithBundleProducts)?.bundleProducts ?? [];

  const hasBundleProducts = bundleProducts.length > 0;

  const getElectionWindowMessage = (
    electionWindow: ElectionWindowInfoModel | undefined,
  ): string | null => {
    if (
      !electionWindow ||
      electionWindow.canElectNow ||
      !electionWindow.messagePayload
    ) {
      return null;
    }

    const payload = electionWindow.messagePayload;

    const getMonthName = (month?: number): string => {
      if (!month) {
        return '';
      }

      const monthKey = String(month) as keyof typeof resources.months;
      return translate(resources.months[monthKey]).toLowerCase();
    };

    // Single election window (1 month)
    if (payload.allowedTimesPerYear === 1 || !payload.secondOrderMonth) {
      return translate(
        resources.cart.errors.electionPeriodExpire.singleElectionWindow,
        {
          3: getMonthName(payload.nextAvailableMonth),
        },
      );
    }

    // Multiple election windows (2 months)
    return translate(
      resources.cart.errors.electionPeriodExpire.multipleElectionWindows,
      {
        0: payload.allowedTimesPerYear,
        1: getMonthName(payload.firstOrderMonth),
        2: getMonthName(payload.secondOrderMonth),
        3: getMonthName(payload.nextAvailableMonth),
      },
    );
  };

  const loadProduct = useCallback(
    async (
      productId: string | undefined,
      abortController?: AbortController,
    ) => {
      if (!/^\d+$/.test(productId || '')) {
        setProduct(undefined);
        navigate('/not-found');
      }

      const result = await productsService.getProduct(Number(productId), {
        signal: abortController?.signal,
      });
      if (result.hasErrors) {
        setErrorType(result.errorType);
      } else {
        setProduct(result.data);
        setTabs(getVisibleTabs(result.data, translate));

        document.title = result.data.name ?? 'Product';

        const productDetails = result.data as DefaultProductDetailsModel;

        setPaymentMethodDetails(productDetails.paymentMethodDetails || []);
        setPaymentMethods(productDetails.paymentMethods || []);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadProduct(productId, controller);

    return () => {
      controller.abort();
    };
  }, [productId, loadProduct]);

  const dyProductPageData = useMemo(
    () => ({
      productId: Number(productId),
    }),
    [productId],
  );

  if (errorType === ErrorType.NotFoundError) return <NotFound />;
  if (errorType === ErrorType.InternalServerError) return <ErrorPanel />;

  return (
    <>
      <Loader
        loading={!product || product.productId !== Number(productId)}
        loader={<LoadingSpinner />}
      >
        {!!product && (
          <>
            <Breadcrumbs
              entries={[
                ...getBreadcrumbs(
                  product.path,
                  crumbs.home,
                  product.productId,
                  product.name,
                ),
              ]}
              className="product-page-breadcrumbs"
            />
            <section className="section-main section-full product-page">
              <div className="container">
                {paymentError && (
                  <Notification
                    type={NotificationType.Error}
                    messages={[
                      {
                        message: paymentError,
                      },
                    ]}
                  />
                )}
                {!platformInfo?.isPlatformOpened &&
                  !isPayableByCard &&
                  product.productTypeId == ProductTypes.DefaultProduct && (
                    <Notification
                      type={NotificationType.Warning}
                      messages={[
                        {
                          message: translate(
                            resources.products.platformClosed.warning,
                          ),
                          onShowDetailsClick: () =>
                            setVisibleDialogId(
                              ProductPageDialogs.PlatformClosed,
                            ),
                        },
                      ]}
                      showClose
                    />
                  )}
                {!product.electionWindowInfoModel.canElectNow && (
                  <Notification
                    type={NotificationType.Error}
                    messages={[
                      {
                        message:
                          getElectionWindowMessage(
                            product.electionWindowInfoModel,
                          ) ?? '',
                      },
                    ]}
                    showClose
                  />
                )}
                {visibleDialogId === ProductPageDialogs.PlatformClosed && (
                  <PlatformClosedDetailsDialog
                    closeDialog={() => setVisibleDialogId(undefined)}
                  />
                )}
                {visibleDialogId === ProductPageDialogs.LimitErrorDetails && (
                  <LimitErrorDetailsDialog
                    closeDialog={() => setVisibleDialogId(undefined)}
                  />
                )}
                {hasValidationErrors(errors, 'null') && (
                  <Notification
                    type={NotificationType.Error}
                    messages={errors.null.map(e => ({
                      message: getErrorMessage(e, translate),
                      onShowDetailsClick: e.payload?.isAutobudgetLimitError
                        ? () =>
                            setVisibleDialogId(
                              ProductPageDialogs.LimitErrorDetails,
                            )
                        : undefined,
                    }))}
                  />
                )}
                <div className="product-main">
                  <h1 className="product-name">{_.unescape(product.name)}</h1>
                  <ProductCard
                    name={product.name ?? ''}
                    imagePath={product.imagePath}
                    tags={product.tags}
                  />
                  <div className="product-info">
                    {!isMobile && hasBundleProducts && (
                      <ProductBundleInfo
                        bundleProducts={bundleProducts}
                        productTypeId={product.productTypeId}
                      />
                    )}
                    {customerInfo.canAccessMyCards && !hasBundleProducts && (
                      <MyCards />
                    )}
                    {!isMobile && <PrivacyInfo />}
                  </div>

                  <ProductDetailsForm
                    {...mapProductDetailsProps(
                      product,
                      paymentMethodDetails,
                      paymentMethods,
                      errors,
                      setErrors,
                      setPaymentError,
                      customerBudget,
                      updateCustomerBudgetCashback,
                    )}
                  />
                </div>

                {hasBundleProducts && (
                  <BundleProductsCarousel bundleProducts={bundleProducts} />
                )}
                {isMobile && hasBundleProducts && (
                  <ProductBundleInfo
                    bundleProducts={bundleProducts}
                    productTypeId={product.productTypeId}
                  />
                )}
                {isMobile && <PrivacyInfo />}
                <DetailsTab tabs={tabs} />

                <RecommendedProducts
                  pageType={DyPageTypeEnum.Product}
                  productPageData={dyProductPageData}
                />
              </div>
            </section>
          </>
        )}
      </Loader>
      <div className="toast-wrapper-bottom">
        <div>
          <SmartNotifications
            productId={Number(productId)}
            categoryId={null}
            isForHome={false}
          />
        </div>
      </div>
    </>
  );
}

const getVisibleTabs = (
  product: ProductDetailsBaseModel,
  translate: any,
): TabInfo[] => {
  const tabs = [
    {
      title: translate(resources.products.description.title),
      details: (
        <div
          dangerouslySetInnerHTML={{
            __html: product?.details?.description ?? '',
          }}
        ></div>
      ),
    },
  ];

  if (product?.details?.howToOrder) {
    tabs.push({
      title: translate(resources.products.howToOrder),
      details: (
        <div
          dangerouslySetInnerHTML={{
            __html: product?.details?.howToOrder ?? '',
          }}
        ></div>
      ),
    });
  }
  if (product?.details?.questionsAndAnswers) {
    tabs.push({
      title: translate(resources.products.questionsAndAnswers),
      details: (
        <div
          dangerouslySetInnerHTML={{
            __html: product?.details?.questionsAndAnswers ?? '',
          }}
        ></div>
      ),
    });
  }

  return tabs;
};
